import { useState, useEffect } from "react";
import { Mail, User, Building, ArrowRight, Check, Plus, Trash2, X } from "lucide-react";
import { Link } from "react-router-dom";
import "../styles/animations.css";
import { useAuth } from "../context/AuthContext";
import ProtectedRoute from "../components/ProtectedRoute";

function OnboardingPageContent() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    companyName: "",
    companySize: "",
    industry: "",
    role: "",
  });
  const [brands, setBrands] = useState<string[]>([""]);
  const [newBrand, setNewBrand] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [showBrandSelector, setShowBrandSelector] = useState(false);
  const [selectedService, setSelectedService] = useState<string>("");
  const [serviceConnections, setServiceConnections] = useState<{[key: string]: {[brandId: string]: boolean}}>({});
  const [connectingService, setConnectingService] = useState<string>("");
  const [brandIds, setBrandIds] = useState<{brandId: string, brandName: string}[]>([]);
  const [notification, setNotification] = useState<{type: 'success' | 'error' | 'warning', message: string} | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  const [touched, setTouched] = useState<{[key: string]: boolean}>({});
  const [preFilledFields, setPreFilledFields] = useState<{[key: string]: boolean}>({});

  // New state for enhanced Step 2 functionality
  const [selectedBrandForConnection, setSelectedBrandForConnection] = useState('');
  const [showConnectionPopup, setShowConnectionPopup] = useState(false);
  const [connectionData, setConnectionData] = useState({ serviceName: '', brandId: '', brandName: '' });
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [connectionStatusLoaded, setConnectionStatusLoaded] = useState(false);
  const [connectingInPopup, setConnectingInPopup] = useState(false);

  // Input fields for different platforms
  const [shopifyDomain, setShopifyDomain] = useState('');
  const [judgemeShopDomain, setJudgemeShopDomain] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [instagramHandle, setInstagramHandle] = useState('');
  const [facebookPageId, setFacebookPageId] = useState('');
  const [tiktokHandle, setTiktokHandle] = useState('');
  const [googleBusinessId, setGoogleBusinessId] = useState('');

  // Add flag to prevent multiple API calls
  const [statusCheckCompleted, setStatusCheckCompleted] = useState(false);

  const { completeOnboarding, isLoading, user, logout, refreshAuth } = useAuth();

  // Load saved data and user info on component mount
  useEffect(() => {
    const savedData = localStorage.getItem('onboarding_data');
    const savedStep = localStorage.getItem('onboarding_step');
    const savedBrands = localStorage.getItem('onboarding_brands');

    // Extract user data first
    let userFirstName = '';
    let userLastName = '';
    let userEmail = '';
    let preFilledState = {};

    if (user) {
      const nameParts = user.name ? user.name.split(' ') : [];
      userFirstName = user.firstName || nameParts[0] || '';
      userLastName = user.lastName || nameParts.slice(1).join(' ') || '';
      userEmail = user.email || '';

      // Mark fields as pre-filled
      preFilledState = {
        email: !!userEmail,
        firstName: !!userFirstName,
        lastName: !!userLastName
      };
      setPreFilledFields(preFilledState);
    }

    // Load saved data and merge with user data
    let finalFormData = {
      firstName: userFirstName,
      lastName: userLastName,
      email: userEmail,
      password: '',
      companyName: '',
      companySize: '',
      industry: '',
      role: ''
    };

    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        finalFormData = {
          ...parsed,
          // User data always takes priority over saved data
          email: userEmail || parsed.email,
          firstName: userFirstName || parsed.firstName,
          lastName: userLastName || parsed.lastName
        };
      } catch (error) {
        console.error('Error loading saved form data:', error);
      }
    }

    setFormData(finalFormData);



    // Check backend for existing onboarding data and determine correct step
    const checkOnboardingStatus = async () => {
      // Prevent multiple calls
      if (statusCheckCompleted) {
        console.log('⚠️ Status check already completed, skipping...');
        return;
      }

      try {
        console.log('🔄 Checking onboarding status (first time)...');
        setStatusCheckCompleted(true);

        const response = await fetch(`${import.meta.env.VITE_API_URL}/onboarding/onboard-status`, {
          method: 'GET',
          credentials: 'include'
        });

        if (response.ok) {
          const backendData = await response.json();
          console.log('📊 Backend onboarding status:', backendData);

          // Check if step 1 is COMPLETE (not just has any data)
          // Step 1 is complete only if we have bizamplyId AND brands AND company name
          const hasCompletedStep1 = (
            backendData.bizamplyId &&
            backendData.brands &&
            backendData.brands.length > 0 &&
            backendData.companyName
          );

          console.log('🔍 Step 1 completion check:', {
            hasCompletedStep1,
            status: backendData.status,
            setupStatus: backendData.setupStatus,
            bizamplyId: !!backendData.bizamplyId,
            brandsLength: backendData.brands?.length,
            hasCompanyName: !!backendData.companyName
          });

          if (hasCompletedStep1) {
            // Pre-fill form with backend data if available
            if (backendData.brands && backendData.brands.length > 0) {
              const brandNames = backendData.brands.map((b: any) => b.BrandName);
              setBrands(brandNames);
              setBrandIds(backendData.brands.map((b: any) => ({
                brandId: b.BrandId,
                brandName: b.BrandName
              })));

              // Store IDs in localStorage for compatibility
              localStorage.setItem('brandIds', JSON.stringify(
                backendData.brands.map((b: any) => ({ brandId: b.BrandId, brandName: b.BrandName }))
              ));
            }

            if (backendData.bizamplyId) {
              localStorage.setItem('bizamplyId', backendData.bizamplyId);
            }

            // Pre-fill company name from localStorage if available
            const savedData = localStorage.getItem('onboarding_data');
            if (savedData) {
              try {
                const parsed = JSON.parse(savedData);
                if (parsed.companyName) {
                  setFormData(prev => ({
                    ...prev,
                    companyName: parsed.companyName
                  }));
                }
              } catch (error) {
                console.error('Error parsing saved company data:', error);
              }
            }

            // Always go to step 2 if step 1 is complete (setupStatus > 0)
            setStep(2);
            return;
          }
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
      }

      // Fallback to localStorage logic if backend check fails
      const bizamplyId = localStorage.getItem('bizamplyId');
      const brandIds = localStorage.getItem('brandIds');

      console.log('🔄 Fallback localStorage check:', {
        bizamplyId: !!bizamplyId,
        brandIds: !!brandIds,
        savedStep
      });

      // If we have bizamplyId and brandIds, step 1 was completed
      if (bizamplyId && brandIds) {
        console.log('✅ Step 1 completed (localStorage), going to step 2');

        // Pre-fill brands from localStorage
        try {
          const parsedBrandIds = JSON.parse(brandIds);
          const brandNames = parsedBrandIds.map((b: any) => b.brandName);
          setBrands(brandNames);
          setBrandIds(parsedBrandIds);
        } catch (error) {
          console.error('Error parsing brandIds:', error);
        }

        setStep(2);
      } else if (savedStep) {
        const savedStepNumber = parseInt(savedStep, 10);
        console.log(`📍 Using saved step: ${savedStepNumber}`);
        setStep(savedStepNumber);
      } else {
        console.log('🆕 Fresh start, going to step 1');
        setStep(1);
      }
    };

    // Only check status if user exists and we haven't checked yet
    if (user && !statusCheckCompleted) {
      checkOnboardingStatus();
    }

    if (savedBrands) {
      try {
        setBrands(JSON.parse(savedBrands));
      } catch (error) {
        console.error('Error loading saved brands:', error);
      }
    }
  }, [user, statusCheckCompleted]);

  // Load service connections and brand IDs
  useEffect(() => {
    const savedBrandIds = localStorage.getItem('brandIds');
    const savedConnections = localStorage.getItem('serviceConnections');

    if (savedBrandIds) {
      try {
        setBrandIds(JSON.parse(savedBrandIds));
      } catch (error) {
        console.error('Error loading brand IDs:', error);
      }
    }

    if (savedConnections) {
      try {
        setServiceConnections(JSON.parse(savedConnections));
      } catch (error) {
        console.error('Error loading service connections:', error);
      }
    }
  }, []);

  // Save service connections to localStorage
  useEffect(() => {
    localStorage.setItem('serviceConnections', JSON.stringify(serviceConnections));
  }, [serviceConnections]);

  // Save data to localStorage whenever formData or brands change
  useEffect(() => {
    localStorage.setItem('onboarding_data', JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    localStorage.setItem('onboarding_brands', JSON.stringify(brands));
  }, [brands]);

  useEffect(() => {
    localStorage.setItem('onboarding_step', step.toString());
  }, [step]);

  // Removed the validation useEffect that was causing loops
  // The validation is now only called when needed (button clicks, form submission)

  // Additional effect to handle step changes
  useEffect(() => {
    console.log('📍 Step changed to:', step);

    // If going to step 1 and we have saved data, ensure it's loaded
    if (step === 1) {
      // Reset connection status when going back to step 1
      setConnectionStatusLoaded(false);
      setLoadingConnections(false);

      const bizamplyId = localStorage.getItem('bizamplyId');
      const brandIds = localStorage.getItem('brandIds');

      // If step 1 was previously completed, load the data
      if (bizamplyId && brandIds) {
        console.log('🔄 Step 1 has saved data, ensuring it\'s loaded');
        fetchAndLoadStep1Data();
      }
    }
  }, [step]);

  // Separate effect for step 2 initialization
  useEffect(() => {
    if (step === 2) {
      console.log('🔄 Step 2 reached, checking brand data...');

      // If no brandIds, try to fetch from backend or localStorage
      if (brandIds.length === 0) {
        console.log('⚠️ No brand data found, attempting to fetch...');

        // Try localStorage first
        const savedBrandIds = localStorage.getItem('brandIds');
        if (savedBrandIds) {
          try {
            const parsedBrandIds = JSON.parse(savedBrandIds);
            setBrandIds(parsedBrandIds);
            console.log('✅ Loaded brand data from localStorage:', parsedBrandIds);
            return; // Exit early if we found data in localStorage
          } catch (error) {
            console.error('Error parsing saved brand IDs:', error);
          }
        }

        // If still no data, fetch from backend
        fetchAndLoadStep1Data();
      } else if (!connectionStatusLoaded) {
        console.log('🔄 Brand data available, initializing step 2...');

        // Auto-select first brand if none selected
        if (!selectedBrandForConnection) {
          setSelectedBrandForConnection(brandIds[0].brandId);
          console.log('🎯 Auto-selected first brand:', brandIds[0].brandName);
        }

        // Load connection status only once when step 2 is reached and not already loaded
        loadConnectionStatus();
      }
    }
  }, [step, brandIds.length, connectionStatusLoaded]); // Include connectionStatusLoaded to prevent reloading

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Don't allow changes to pre-filled fields
    if (preFilledFields[name]) {
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Real-time validation for email
    if (name === 'email' && touched[name]) {
      const error = validateField(name, value);
      setFieldErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const addBrand = () => {
    if (newBrand.trim() && !brands.includes(newBrand.trim())) {
      setBrands([...brands.filter(b => b.trim() !== ""), newBrand.trim()]);
      setNewBrand("");
    }
  };

  const removeBrand = (index: number) => {
    setBrands(brands.filter((_, i) => i !== index));
  };

  const updateBrand = (index: number, value: string) => {
    const updatedBrands = [...brands];
    updatedBrands[index] = value;
    setBrands(updatedBrands);
  };

  const saveStepData = async (currentStep: number) => {
    setIsSaving(true);
    try {
      if (currentStep === 1) {
        // Save personal info and brands to backend after step 1
        const validBrands = brands.filter(brand => brand.trim() !== "");
        if (formData.companyName && validBrands.length > 0) {
          const onboardingData = {
            company: formData.companyName,
            brands: validBrands,
            personalInfo: {
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email
            },
            isUpdate: true // ← Flag to indicate this might be an update
          };

          console.log('💾 Saving/Updating step 1 data to database:', onboardingData);

          const token = localStorage.getItem('auth_token');
          const headers = {
            'Content-Type': 'application/json'
          };

          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }

          const response = await fetch(`${import.meta.env.VITE_API_URL}/onboarding/submit-user`, {
            method: 'POST',
            headers,
            body: JSON.stringify(onboardingData),
            credentials: 'include'
          });

          if (!response.ok) {
            if (response.status === 401) {
              console.log('🔑 Auth token invalid for step 1 submission, continuing without backend save');
              // Don't throw error, just log it and continue
              return;
            }

            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to save step 1 data to database');
          }

          const result = await response.json();
          const operationType = result.operation || 'saved';
          console.log(`✅ Step 1 data ${operationType} to database successfully:`, result);

          // CRITICAL: Save to localStorage as backup for data persistence
          localStorage.setItem('onboarding_data', JSON.stringify(formData));
          localStorage.setItem('onboarding_brands', JSON.stringify(validBrands));
          console.log('💾 Backup saved to localStorage:', {
            formData: formData,
            brands: validBrands
          });

          // Store the returned IDs for later use
          if (result.bizamplyId) {
            localStorage.setItem('bizamplyId', result.bizamplyId);
            console.log('🆔 Saved bizamplyId:', result.bizamplyId);
          }
          if (result.brandIds) {
            localStorage.setItem('brandIds', JSON.stringify(result.brandIds));
            setBrandIds(result.brandIds);
            console.log('🏷️ Saved brandIds:', result.brandIds);
          }
        }
      }
    } catch (error) {
      console.error('❌ Error saving step data to database:', error);
      // Don't prevent navigation, just log the error
    } finally {
      setIsSaving(false);
    }
  };



  const handleNextStep = async () => {
    // Validate Step 1 fields
    if (step === 1) {
      const errors: {[key: string]: string} = {};

      // Validate all required fields
      if (!formData.firstName.trim()) errors.firstName = 'First name is required';
      if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
      if (!formData.email.trim()) errors.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Please enter a valid email address';
      if (!formData.companyName.trim()) errors.companyName = 'Company name is required';

      const validBrands = brands.filter(brand => brand.trim() !== "");
      if (validBrands.length === 0) errors.brands = 'At least one brand name is required';

      // Mark all fields as touched and set errors
      setTouched({
        firstName: true,
        lastName: true,
        email: true,
        companyName: true,
        brands: true
      });
      setFieldErrors(errors);

      // Don't proceed if there are errors
      if (Object.keys(errors).length > 0) return;
    }

    // Validate Step 2 connections
    if (step === 2) {
      if (!canProceedToStep3()) {
        // Don't proceed if connections are not complete
        return;
      }
    }

    const currentStep = step;
    const nextStep = step + 1;

    console.log(`➡️ Going from step ${currentStep} to step ${nextStep}`);

    // Save current step data
    await saveStepData(currentStep);

    // If going to step 2, fetch latest data to ensure dropdown is populated
    if (nextStep === 2) {
      console.log('🔄 Going to step 2, fetching latest data...');
      await fetchLatestDataForStep2();
    }

    setStep(nextStep);
  };

  // Function to skip onboarding and go directly to dashboard
  const handleSkipOnboarding = async () => {
    try {
      console.log('⏭️ Skipping onboarding...');

      // Mark onboarding as completed in the auth context
      await completeOnboarding();

      // Clear any saved onboarding data
      localStorage.removeItem('onboarding_data');
      localStorage.removeItem('onboarding_brands');
      localStorage.removeItem('onboarding_step');
      localStorage.removeItem('brandIds');
      localStorage.removeItem('serviceConnections');

      console.log('✅ Onboarding skipped successfully');
    } catch (error) {
      console.error('❌ Error skipping onboarding:', error);
      // Still proceed to dashboard even if there's an error
      await completeOnboarding();
    }
  };

  const handlePrevStep = async () => {
    const newStep = step - 1;

    // If going back to step 1, fetch and load the saved data
    if (newStep === 1) {
      console.log('⬅️ Going back to step 1, fetching saved data...');
      await fetchAndLoadStep1Data();
    }

    setStep(newStep);
  };

  // Function to fetch latest data for step 2
  const fetchLatestDataForStep2 = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        console.log('⚠️ No auth token found, skipping backend fetch');
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/onboarding/onboard-status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const backendData = await response.json();
        console.log('📊 Latest data for step 2:', backendData);

        // Update brands and company data for step 2 dropdown
        if (backendData.brands && backendData.brands.length > 0) {
          const brandNames = backendData.brands.map((b: any) => b.BrandName);
          setBrands(brandNames);
          setBrandIds(backendData.brands.map((b: any) => ({
            brandId: b.BrandId,
            brandName: b.BrandName
          })));

          console.log('✅ Updated step 2 dropdown data:', { brands: brandNames });
        }
      } else if (response.status === 401) {
        console.log('🔑 Auth token invalid for onboard-status, user needs to re-login');
        // Don't throw error, just log it
      } else {
        console.log('⚠️ Failed to fetch onboard-status:', response.status);
      }
    } catch (error) {
      console.error('❌ Error fetching step 2 data:', error);
      // Don't throw error, just log it
    }
  };

  // Function to load connection status for all brands and services
  const loadConnectionStatus = async () => {
    // Prevent multiple simultaneous calls
    if (loadingConnections || connectionStatusLoaded) {
      console.log('⚠️ Connection status already loading or loaded, skipping...');
      return;
    }

    try {
      setLoadingConnections(true);
      console.log('🔄 Loading connection status for brands:', brandIds);

      if (brandIds.length === 0) {
        console.log('⚠️ No brands available for connection status check');
        setLoadingConnections(false);
        return;
      }

      const newConnections: {[key: string]: {[brandId: string]: boolean}} = {
        shopify: {},
        judgeme: {},
        yotpo: {},
        whatsapp: {},
        instagram: {},
        facebook: {},
        tiktok: {},
        google: {}
      };

      // Batch check connection status for all brands and services
      const services = ['shopify', 'judgeme', 'yotpo'];
      const connectionPromises: Promise<void>[] = [];

      for (const brand of brandIds) {
        console.log(`🔍 Checking connections for brand: ${brand.brandName} (${brand.brandId})`);

        for (const service of services) {
          const promise = (async () => {
            try {
              const response = await fetch(`${import.meta.env.VITE_API_URL}/integrations/${service}/check-connection`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ brandId: brand.brandId }),
                credentials: 'include'
              });

              if (response.ok) {
                const data = await response.json();
                newConnections[service][brand.brandId] = data.connected || false;
                console.log(`✅ ${service} status for ${brand.brandName}:`, data.connected);
              } else {
                newConnections[service][brand.brandId] = false;
                console.log(`⚠️ ${service} check failed for ${brand.brandName}:`, response.status);
              }
            } catch (error) {
              console.error(`❌ Error checking ${service} for ${brand.brandName}:`, error);
              newConnections[service][brand.brandId] = false;
            }
          })();

          connectionPromises.push(promise);
        }
      }

      // Wait for all connection checks to complete
      await Promise.all(connectionPromises);
      console.log('🎯 All connection status checks completed');

      setServiceConnections(newConnections);
      setConnectionStatusLoaded(true);
      console.log('✅ Connection status loaded:', newConnections);

    } catch (error) {
      console.error('❌ Error loading connection status:', error);
    } finally {
      setLoadingConnections(false);
    }
  };

  // Function to handle service connection
  const handleServiceConnection = (serviceName: string, brandId: string, brandName: string) => {
    console.log(`🔗 Initiating ${serviceName} connection for ${brandName} (${brandId})`);

    setConnectionData({ serviceName, brandId, brandName });
    setShowConnectionPopup(true);

    // Reset all form fields
    setShopifyDomain('');
    setJudgemeShopDomain('');
    setWhatsappNumber('');
    setInstagramHandle('');
    setFacebookPageId('');
    setTiktokHandle('');
    setGoogleBusinessId('');
  };

  // Function to handle popup connection submission
  const handlePopupConnection = async () => {
    if (connectingInPopup) {
      console.log('⚠️ Already connecting, please wait...');
      return;
    }

    try {
      setConnectingInPopup(true);
      const { serviceName, brandId, brandName } = connectionData;
      console.log(`🚀 Connecting ${serviceName} for ${brandName}...`);

      let connectionUrl = '';
      let requestBody: any = { brandId };
      let validationError = '';

      switch (serviceName) {
        case 'shopify':
          const domain = shopifyDomain.trim();
          if (!domain) {
            validationError = 'Please enter your Shopify domain';
          } else if (!domain.includes('.myshopify.com') && !domain.includes('.shopify.com')) {
            validationError = 'Please enter a valid Shopify domain (e.g., yourstore.myshopify.com)';
          }
          if (validationError) {
            setNotification({ type: 'error', message: validationError });
            setTimeout(() => setNotification(null), 3000);
            setConnectingInPopup(false);
            return;
          }
          // Use existing backend OAuth flow
          connectionUrl = `${process.env.NEXT_PUBLIC_API_URL}/integration/shopify/?company=${brandId}&shop=${domain}`;
          // Open in popup window for OAuth flow
          const shopifyPopup = window.open(connectionUrl, '_blank', 'width=600,height=700,scrollbars=yes,resizable=yes');

          // Monitor popup for completion
          const checkClosed = setInterval(() => {
            if (shopifyPopup?.closed) {
              clearInterval(checkClosed);
              // Check connection status after popup closes
              setTimeout(() => {
                checkServiceConnection('shopify', brandId);
              }, 1000);
            }
          }, 1000);

          setConnectingInPopup(false);
          setShowConnectionPopup(false);
          return;

        case 'judgeme':
          // No domain input needed - Judge.me will auto-fetch from Shopify data
          // Use existing backend OAuth flow
          connectionUrl = `${process.env.NEXT_PUBLIC_API_URL}/integration/judgeme/?company=${brandId}`;
          // Open in popup window for OAuth flow
          const judgemePopup = window.open(connectionUrl, '_blank', 'width=600,height=700,scrollbars=yes,resizable=yes');

          // Monitor popup for completion
          const checkJudgeClosed = setInterval(() => {
            if (judgemePopup?.closed) {
              clearInterval(checkJudgeClosed);
              // Check connection status after popup closes
              setTimeout(() => {
                checkServiceConnection('judgeme', brandId);
              }, 1000);
            }
          }, 1000);

          setConnectingInPopup(false);
          setShowConnectionPopup(false);
          return;

        case 'yotpo':
          // Use existing backend OAuth flow
          connectionUrl = `${process.env.NEXT_PUBLIC_API_URL}/integration/yotpo/auth?company=${brandId}`;
          // Open in popup window for OAuth flow
          const yotpoPopup = window.open(connectionUrl, '_blank', 'width=600,height=700,scrollbars=yes,resizable=yes');

          // Monitor popup for completion
          const checkYotpoClosed = setInterval(() => {
            if (yotpoPopup?.closed) {
              clearInterval(checkYotpoClosed);
              // Check connection status after popup closes
              setTimeout(() => {
                checkServiceConnection('yotpo', brandId);
              }, 1000);
            }
          }, 1000);

          setConnectingInPopup(false);
          setShowConnectionPopup(false);
          return;

        case 'instagram':
          // Use existing backend OAuth flow
          connectionUrl = `${process.env.NEXT_PUBLIC_API_URL}/integration/instagram?company=${brandId}`;
          // Open in popup window for OAuth flow
          const instagramPopup = window.open(connectionUrl, '_blank', 'width=600,height=700,scrollbars=yes,resizable=yes');

          // Monitor popup for completion
          const checkInstagramClosed = setInterval(() => {
            if (instagramPopup?.closed) {
              clearInterval(checkInstagramClosed);
              // Check connection status after popup closes
              setTimeout(() => {
                checkServiceConnection('instagram', brandId);
              }, 1000);
            }
          }, 1000);

          setConnectingInPopup(false);
          setShowConnectionPopup(false);
          return;

        case 'whatsapp':
          const phone = whatsappNumber.trim();
          if (!phone) {
            validationError = 'Please enter your WhatsApp Business number';
          } else if (!/^\+\d{10,15}$/.test(phone)) {
            validationError = 'Please enter a valid phone number with country code (e.g., +1234567890)';
          }
          if (validationError) {
            setNotification({ type: 'error', message: validationError });
            setTimeout(() => setNotification(null), 3000);
            setConnectingInPopup(false);
            return;
          }
          connectionUrl = `${process.env.NEXT_PUBLIC_API_URL}/integrations/whatsapp/connect`;
          requestBody.phoneNumber = phone;
          break;



        case 'facebook':
          const pageId = facebookPageId.trim();
          if (!pageId) {
            validationError = 'Please enter your Facebook Page ID';
          } else if (!/^\d{15,16}$/.test(pageId)) {
            validationError = 'Please enter a valid Facebook Page ID (15-16 digits)';
          }
          if (validationError) {
            setNotification({ type: 'error', message: validationError });
            setTimeout(() => setNotification(null), 3000);
            setConnectingInPopup(false);
            return;
          }
          connectionUrl = `${process.env.NEXT_PUBLIC_API_URL}/integrations/facebook/connect`;
          requestBody.pageId = pageId;
          break;

        case 'tiktok':
          const tiktokUser = tiktokHandle.trim();
          if (!tiktokUser) {
            validationError = 'Please enter your TikTok handle';
          } else if (!tiktokUser.startsWith('@')) {
            validationError = 'TikTok handle must start with @ (e.g., @yourbrand)';
          }
          if (validationError) {
            setNotification({ type: 'error', message: validationError });
            setTimeout(() => setNotification(null), 3000);
            setConnectingInPopup(false);
            return;
          }
          connectionUrl = `${process.env.NEXT_PUBLIC_API_URL}/integrations/tiktok/connect`;
          requestBody.handle = tiktokUser;
          break;

        case 'google':
          const gaId = googleBusinessId.trim();
          if (!gaId) {
            validationError = 'Please enter your Google Analytics Property ID';
          } else if (!/^GA4-[A-Z0-9]+-\d+$/.test(gaId)) {
            validationError = 'Please enter a valid GA4 Property ID (e.g., GA4-XXXXXXXXX-1)';
          }
          if (validationError) {
            setNotification({ type: 'error', message: validationError });
            setTimeout(() => setNotification(null), 3000);
            setConnectingInPopup(false);
            return;
          }
          connectionUrl = `${process.env.NEXT_PUBLIC_API_URL}/integrations/google/connect`;
          requestBody.businessId = gaId;
          break;

        default:
          console.error('Unknown service:', serviceName);
          setConnectingInPopup(false);
          return;
      }

      console.log(`📤 Sending connection request to ${connectionUrl}:`, requestBody);

      const response = await fetch(connectionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ ${serviceName} connection successful:`, result);

        // Update connection status
        setServiceConnections(prev => ({
          ...prev,
          [serviceName]: {
            ...prev[serviceName],
            [brandId]: true
          }
        }));

        // Show success notification
        setNotification({
          type: 'success',
          message: `${serviceName.charAt(0).toUpperCase() + serviceName.slice(1)} connected successfully for ${brandName}!`
        });

        // Clear notification after 5 seconds
        setTimeout(() => setNotification(null), 5000);

        // Close popup after a brief delay to show success
        setTimeout(() => {
          setShowConnectionPopup(false);
          setConnectingInPopup(false);
        }, 1500);

        // Check if we can proceed to step 3 and auto-advance
        setTimeout(() => {
          if (canProceedToStep3()) {
            console.log('🎉 Minimum requirements met, can proceed to Step 3');
            // Auto-advance to step 3 after successful connection
            setStep(3);
          }
        }, 2000);

      } else {
        const errorData = await response.json();
        console.error(`❌ ${serviceName} connection failed:`, errorData);
        setNotification({
          type: 'error',
          message: `Failed to connect ${serviceName}: ${errorData.message || 'Unknown error'}`
        });
        setTimeout(() => setNotification(null), 5000);
        setConnectingInPopup(false);
      }

    } catch (error) {
      console.error('❌ Connection error:', error);
      setNotification({
        type: 'error',
        message: 'Connection failed. Please try again.'
      });
      setTimeout(() => setNotification(null), 5000);
      setConnectingInPopup(false);
    }
  };

  // Function to fetch and load step 1 data when going back
  const fetchAndLoadStep1Data = async () => {
    try {
      console.log('🔄 Fetching step 1 data from backend...');

      // First try to get data from backend
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/onboarding/onboard-status`, {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const backendData = await response.json();
        console.log('📊 Backend data for step 1:', backendData);

        // If backend has data, use it
        if (backendData.status === 'success' && (backendData.brands || backendData.setupStatus > 0)) {
          console.log('📊 Backend response details:', {
            status: backendData.status,
            setupStatus: backendData.setupStatus,
            brands: backendData.brands,
            bizamplyId: backendData.bizamplyId
          });

          // Load brands from backend
          if (backendData.brands && backendData.brands.length > 0) {
            const brandNames = backendData.brands.map((b: any) => b.BrandName);
            setBrands(brandNames);
            setBrandIds(backendData.brands.map((b: any) => ({
              brandId: b.BrandId,
              brandName: b.BrandName
            })));

            console.log('✅ Loaded brands from backend:', brandNames);
          }

          // Load company name from backend (now included in response)
          let companyName = '';
          if (backendData.companyName) {
            companyName = backendData.companyName;
            console.log('✅ Loaded company name from backend:', companyName);
          } else {
            // Fallback to localStorage if backend doesn't have it
            const savedData = localStorage.getItem('onboarding_data');
            if (savedData) {
              try {
                const parsed = JSON.parse(savedData);
                companyName = parsed.companyName || '';
                console.log('📋 Fallback: Loaded company name from localStorage:', companyName);
              } catch (error) {
                console.error('Error parsing saved company data:', error);
              }
            }
          }

          // Update form data with company name
          setFormData(prev => ({
            ...prev,
            companyName: companyName
          }));

          console.log('✅ Final loaded data:', {
            companyName,
            brands: backendData.brands?.map((b: any) => b.BrandName) || []
          });
          return;
        }
      }

      // Fallback to localStorage if backend fails
      console.log('📁 Falling back to localStorage for step 1 data');

      const savedData = localStorage.getItem('onboarding_data');
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          console.log('📋 Found saved form data:', parsed);

          setFormData(prev => ({
            ...prev,
            companyName: parsed.companyName || prev.companyName,
          }));
        } catch (error) {
          console.error('Error loading saved form data:', error);
        }
      }

      const savedBrands = localStorage.getItem('onboarding_brands');
      if (savedBrands) {
        try {
          const parsedBrands = JSON.parse(savedBrands);
          console.log('🏷️ Found saved brands:', parsedBrands);
          setBrands(parsedBrands);
        } catch (error) {
          console.error('Error loading saved brands:', error);
        }
      }

      const savedBrandIds = localStorage.getItem('brandIds');
      if (savedBrandIds) {
        try {
          const parsedBrandIds = JSON.parse(savedBrandIds);
          console.log('🆔 Found saved brand IDs:', parsedBrandIds);
          setBrandIds(parsedBrandIds);
        } catch (error) {
          console.error('Error loading saved brand IDs:', error);
        }
      }

    } catch (error) {
      console.error('❌ Error fetching step 1 data:', error);

      // Final fallback to localStorage only
      const savedData = localStorage.getItem('onboarding_data');
      const savedBrands = localStorage.getItem('onboarding_brands');

      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          setFormData(prev => ({ ...prev, companyName: parsed.companyName || '' }));
        } catch (error) {
          console.error('Error in final fallback:', error);
        }
      }

      if (savedBrands) {
        try {
          setBrands(JSON.parse(savedBrands));
        } catch (error) {
          console.error('Error in final brands fallback:', error);
        }
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Prepare data for backend
      const validBrands = brands.filter(brand => brand.trim() !== "");
      const onboardingData = {
        company: formData.companyName,
        brands: validBrands.length > 0 ? validBrands : [formData.companyName],
        personalInfo: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email
        }
      };

      console.log('🚀 Submitting onboarding data:', onboardingData);

      // Submit to backend
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/onboarding/submit-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(onboardingData),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Onboarding submission failed');
      }

      const result = await response.json();
      console.log('✅ Onboarding submitted successfully:', result);

      // Complete the setup
      const completeResponse = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/onboarding/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!completeResponse.ok) {
        throw new Error('Failed to complete onboarding setup');
      }

      console.log('✅ Onboarding completed successfully');

      // Clear onboarding data from localStorage
      localStorage.removeItem('onboarding_data');
      localStorage.removeItem('onboarding_brands');
      localStorage.removeItem('onboarding_step');

      // Complete onboarding and redirect to dashboard
      completeOnboarding();
    } catch (error) {
      console.error('❌ Onboarding failed:', error);
      // You might want to show an error message to the user here
    }
  };

  // Notification helper
  const showNotification = (type: 'success' | 'error' | 'warning', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000); // Auto-hide after 5 seconds
  };

  // Field validation functions
  const validateField = (name: string, value: string) => {
    switch (name) {
      case 'firstName':
        return value.trim() ? '' : 'First name is required';
      case 'lastName':
        return value.trim() ? '' : 'Last name is required';
      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
        return '';
      case 'companyName':
        return value.trim() ? '' : 'Company name is required';
      default:
        return '';
    }
  };

  const validateBrands = () => {
    const validBrands = brands.filter(brand => brand.trim() !== "");
    return validBrands.length > 0 ? '' : 'At least one brand name is required';
  };

  const handleFieldBlur = (name: string) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, formData[name as keyof typeof formData]);
    setFieldErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleBrandBlur = () => {
    setTouched(prev => ({ ...prev, brands: true }));
    const error = validateBrands();
    setFieldErrors(prev => ({ ...prev, brands: error }));
  };

  // Validation functions
  const isStep1Complete = () => {
    // Check if all required fields have values (including pre-filled ones)
    const hasValidFields = formData.firstName.trim() &&
           formData.lastName.trim() &&
           formData.email.trim() &&
           formData.companyName.trim() &&
           brands.some(brand => brand.trim() !== "");

    // Only check errors for fields that are not pre-filled and have been touched
    const relevantErrors = Object.entries(fieldErrors).filter(([fieldName, error]) => {
      // Skip validation errors for pre-filled fields
      if (preFilledFields[fieldName]) return false;
      // Only consider errors for touched fields or required fields
      return error && (touched[fieldName] || ['companyName'].includes(fieldName));
    });

    const hasNoRelevantErrors = relevantErrors.length === 0 && !validateBrands();

    // Removed console.log to prevent spam - validation is working silently

    return hasValidFields && hasNoRelevantErrors;
  };

  // Helper function to check if Shopify is connected for selected brand
  const isShopifyConnectedForBrand = (brandId: string) => {
    return serviceConnections.shopify?.[brandId] === true;
  };

  // Helper function to check if any review platform is connected for selected brand
  const isReviewPlatformConnectedForBrand = (brandId: string) => {
    return serviceConnections.judgeme?.[brandId] === true ||
           serviceConnections.yotpo?.[brandId] === true;
  };

  // Legacy functions for backward compatibility
  const isShopifyConnected = () => {
    return brandIds.some(brand =>
      serviceConnections.shopify?.[brand.brandId] === true
    );
  };

  const isJudgemeOrYotpoConnected = () => {
    return brandIds.some(brand =>
      serviceConnections.judgeme?.[brand.brandId] === true ||
      serviceConnections.yotpo?.[brand.brandId] === true
    );
  };

  const canProceedToStep3 = () => {
    // At least one brand must have Shopify + (Judge.me OR Yotpo) connected
    return brandIds.some(brand =>
      isShopifyConnectedForBrand(brand.brandId) &&
      isReviewPlatformConnectedForBrand(brand.brandId)
    );
  };

  // Service connection handlers
  const handleServiceConnect = (serviceName: string) => {
    if (brandIds.length === 0) {
      return; // Button should be disabled if no brands
    }

    if (brandIds.length === 1) {
      // Only one brand, connect directly
      connectService(serviceName, brandIds[0].brandId, brandIds[0].brandName);
    } else {
      // Multiple brands, show selector
      setSelectedService(serviceName);
      setShowBrandSelector(true);
    }
  };

  const connectService = async (serviceName: string, brandId: string, brandName: string) => {
    setConnectingService(serviceName);

    try {
      let connectionUrl = '';

      switch (serviceName.toLowerCase()) {
        case 'shopify':
          const shopDomain = prompt(`Enter your Shopify store domain for ${brandName}:\n(e.g., yourstore.myshopify.com)`);
          if (!shopDomain) {
            setConnectingService('');
            return;
          }
          connectionUrl = `${process.env.NEXT_PUBLIC_API_URL}/integration/shopify?company=${brandId}&shop=${shopDomain}`;
          break;

        case 'judgeme':
          // No shop domain needed - Judge.me will auto-fetch from Shopify data
          connectionUrl = `${process.env.NEXT_PUBLIC_API_URL}/integration/judgeme?company=${brandId}`;
          break;

        case 'yotpo':
          connectionUrl = `${process.env.NEXT_PUBLIC_API_URL}/integration/yotpo/auth?company=${brandId}`;
          break;

        case 'instagram':
          connectionUrl = `${process.env.NEXT_PUBLIC_API_URL}/integration/instagram?company=${brandId}`;
          break;

        case 'tiktok':
        case 'facebook':
          showNotification('warning', `${serviceName} integration coming soon!`);
          setConnectingService('');
          return;

        default:
          showNotification('error', `Unknown service: ${serviceName}`);
          setConnectingService('');
          return;
      }

      // Open connection URL in new window
      const popup = window.open(connectionUrl, '_blank', 'width=600,height=700,scrollbars=yes,resizable=yes');

      // Monitor popup for completion
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          // Check connection status after popup closes
          setTimeout(() => {
            checkServiceConnection(serviceName, brandId);
          }, 1000);
        }
      }, 1000);

    } catch (error) {
      console.error(`Error connecting ${serviceName}:`, error);
      showNotification('error', `Failed to connect ${serviceName}. Please try again.`);
    } finally {
      setConnectingService('');
      setShowBrandSelector(false);
    }
  };

  const checkServiceConnection = async (serviceName: string, brandId: string) => {
    try {
      let endpoint = '';

      // Use specific status endpoints for each service
      switch (serviceName.toLowerCase()) {
        case 'shopify':
          endpoint = `${process.env.NEXT_PUBLIC_API_URL}/integration/shopify/status/${brandId}`;
          break;
        case 'judgeme':
          endpoint = `${process.env.NEXT_PUBLIC_API_URL}/integration/judgeme/status/${brandId}`;
          break;
        case 'yotpo':
          endpoint = `${process.env.NEXT_PUBLIC_API_URL}/integration/yotpo/status/${brandId}`;
          break;
        case 'instagram':
          endpoint = `${process.env.NEXT_PUBLIC_API_URL}/integration/instagram/status/${brandId}`;
          break;
        default:
          // Fallback to general check-connection endpoint
          endpoint = `${process.env.NEXT_PUBLIC_API_URL}/onboarding/check-connection`;
      }

      let response;
      if (serviceName.toLowerCase() === 'shopify' || serviceName.toLowerCase() === 'judgeme' || serviceName.toLowerCase() === 'yotpo' || serviceName.toLowerCase() === 'instagram') {
        // Use GET request for specific service status endpoints
        response = await fetch(endpoint, {
          method: 'GET',
          credentials: 'include'
        });
      } else {
        // Use POST request for general check-connection endpoint
        response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ serviceName, brandId })
        });
      }

      if (response.ok) {
        const result = await response.json();
        if (result.connected) {
          // Update connection status
          setServiceConnections(prev => ({
            ...prev,
            [serviceName]: {
              ...prev[serviceName],
              [brandId]: true
            }
          }));

          // Show success notification
          setTimeout(() => {
            showNotification('success', `${serviceName.charAt(0).toUpperCase() + serviceName.slice(1)} connected successfully!`);
          }, 500);
        }
      }
    } catch (error) {
      console.error('Error checking connection status:', error);
    }
  };

  // Skip to dashboard function - improved version
  const handleSkipToDashboard = async () => {
    console.log('🚀 SKIP: Skipping onboarding and going to dashboard');

    try {
      // Use the improved skip function
      await handleSkipOnboarding();
    } catch (error) {
      console.error('❌ Error during skip, forcing redirect:', error);

      // Fallback: Set localStorage flags and redirect
      localStorage.setItem('onboarding_completed', 'true');
      localStorage.setItem('skip_onboarding', 'true');

      // Clear any existing onboarding data
      localStorage.removeItem('onboarding_data');
      localStorage.removeItem('onboarding_brands');
      localStorage.removeItem('onboarding_step');
      localStorage.removeItem('brandIds');
      localStorage.removeItem('serviceConnections');

      console.log('🚀 TRYING MULTIPLE REDIRECT METHODS...');

      // Try multiple redirect methods
      try {
        // Method 1: window.location.replace
        window.location.replace('/dashboard');
      } catch (e) {
        console.log('Method 1 failed, trying method 2');
        try {
          // Method 2: window.location.href
          window.location.href = '/dashboard';
        } catch (e2) {
          console.log('Method 2 failed, trying method 3');
          // Method 3: Force page reload to dashboard
          window.location.assign('/dashboard');
        }
      }
    }
  };

  const isServiceConnected = (serviceName: string, brandId?: string) => {
    if (!brandId && brandIds.length > 0) {
      // Check if connected to any brand
      return brandIds.some(brand => serviceConnections[serviceName]?.[brand.brandId]);
    }
    return brandId ? serviceConnections[serviceName]?.[brandId] : false;
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-white relative overflow-hidden">
      {/* Notification */}
      {notification && (
        <div
          className={`fixed top-4 right-4 z-[99999] p-4 rounded-lg shadow-2xl max-w-md animate-fadeInScale border-2 ${
            notification.type === 'success' ? 'bg-green-600 text-white border-green-500' :
            notification.type === 'error' ? 'bg-red-600 text-white border-red-500' :
            'bg-yellow-600 text-white border-yellow-500'
          }`}
          style={{ zIndex: 99999 }}
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              {notification.type === 'success' && <Check className="h-5 w-5" />}
              {notification.type === 'error' && <X className="h-5 w-5" />}
              {notification.type === 'warning' && <ArrowRight className="h-5 w-5" />}
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{notification.message}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setNotification(null)}
                className="inline-flex text-white hover:text-gray-200 focus:outline-none"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Background texture/pattern */}
      <div className="absolute inset-0 z-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/20 via-gray-700/20 to-gray-500/20 dark:from-gray-100/20 dark:via-gray-300/20 dark:to-gray-500/20"></div>
        <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.2" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-pattern)" />
        </svg>
        <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-gray-50 dark:from-gray-950 to-transparent z-10"></div>
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-gray-50 dark:from-gray-950 to-transparent z-10"></div>
      </div>

      {/* Left side - Brand/Logo */}
      <div className="w-full md:w-1/2 flex flex-col justify-center items-center p-8 md:p-12 relative z-10">
        <div className="max-w-md w-full space-y-8 animate-fadeIn">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="h-16 w-16 rounded-xl bg-gray-900 dark:bg-gray-100 flex items-center justify-center text-white dark:text-gray-900 font-bold text-2xl shadow-lg animate-fadeInScale">
                B
              </div>
            </div>
            <h2 className="mt-6 text-3xl font-extrabold">BIZAMPLY</h2>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Analytics Platform</p>
          </div>

          <div className="hidden md:block mt-8">
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-md bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900">
                    <Check className="h-6 w-6" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium">Real-time Analytics</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Monitor your business performance with real-time dashboards and insights.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-md bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900">
                    <Check className="h-6 w-6" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium">Customizable Reports</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Create and share custom reports tailored to your business needs.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-md bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900">
                    <Check className="h-6 w-6" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium">Seamless Integrations</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Connect with your favorite tools and services for a unified workflow.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <img className="h-10 w-10 rounded-full" src="https://randomuser.me/api/portraits/women/17.jpg" alt="Testimonial" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium">Sarah Johnson</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Marketing Director, TechCorp</p>
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 italic">
                "Monolith has transformed how we analyze our marketing campaigns. The insights we've gained have directly contributed to a 40% increase in ROI."
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Onboarding Form - Full width on mobile */}
      <div className="w-full md:w-1/2 flex flex-col justify-center items-center p-4 md:p-8 lg:p-12 relative z-10">
        <div className="bg-white dark:bg-gray-900 p-8 rounded-xl shadow-lg max-w-md w-full animate-fadeInScale">
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-1">Create your account</h2>
            <p className="text-gray-500 dark:text-gray-400">
              {step === 1 ? "Enter your personal information" : "Tell us about your company"}
            </p>
          </div>

          {/* Progress indicator */}
          <div className="mb-8">
            <div className="flex items-center">
              <div className={`flex items-center justify-center h-8 w-8 rounded-full ${step >= 1 ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900' : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500'}`}>
                1
              </div>
              <div className={`flex-1 h-1 mx-2 ${step >= 2 ? 'bg-gray-900 dark:bg-gray-100' : 'bg-gray-200 dark:bg-gray-700'}`}></div>
              <div className={`flex items-center justify-center h-8 w-8 rounded-full ${step >= 2 ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900' : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500'}`}>
                2
              </div>
              <div className={`flex-1 h-1 mx-2 ${step >= 3 ? 'bg-gray-900 dark:bg-gray-100' : 'bg-gray-200 dark:bg-gray-700'}`}></div>
              <div className={`flex items-center justify-center h-8 w-8 rounded-full ${step >= 3 ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900' : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500'}`}>
                3
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Step 1: Personal Information */}
            {step === 1 && (
              <>
                {/* Pre-filled Info Message */}
                {(preFilledFields.email || preFilledFields.firstName || preFilledFields.lastName) && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <div className="flex items-center">
                      <Check className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2" />
                      <p className="text-sm text-blue-600 dark:text-blue-400">
                        Some information has been pre-filled from your account. You can only edit the remaining fields.
                      </p>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      First Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="firstName"
                        name="firstName"
                        type="text"
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        onBlur={() => handleFieldBlur('firstName')}
                        readOnly={preFilledFields.firstName}
                        className={`pl-10 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                          preFilledFields.firstName
                            ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed border-gray-200 dark:border-gray-600 pr-10'
                            : fieldErrors.firstName && touched.firstName
                            ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-red-500 focus:ring-red-500'
                            : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-gray-300 dark:focus:ring-gray-600'
                        }`}
                        placeholder="John"
                      />
                      {preFilledFields.firstName && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <Check className="h-4 w-4 text-green-500" />
                        </div>
                      )}
                    </div>
                    {fieldErrors.firstName && touched.firstName && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.firstName}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Last Name
                    </label>
                    <div className="relative">
                      <input
                        id="lastName"
                        name="lastName"
                        type="text"
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        onBlur={() => handleFieldBlur('lastName')}
                        readOnly={preFilledFields.lastName}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                          preFilledFields.lastName
                            ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed border-gray-200 dark:border-gray-600 pr-10'
                            : fieldErrors.lastName && touched.lastName
                            ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-red-500 focus:ring-red-500'
                            : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-gray-300 dark:focus:ring-gray-600'
                        }`}
                        placeholder="Doe"
                      />
                      {preFilledFields.lastName && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <Check className="h-4 w-4 text-green-500" />
                        </div>
                      )}
                    </div>
                    {fieldErrors.lastName && touched.lastName && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.lastName}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={() => handleFieldBlur('email')}
                      readOnly={preFilledFields.email}
                      className={`pl-10 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                        preFilledFields.email
                          ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed border-gray-200 dark:border-gray-600 pr-10'
                          : fieldErrors.email && touched.email
                          ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-red-500 focus:ring-red-500'
                          : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-gray-300 dark:focus:ring-gray-600'
                      }`}
                      placeholder="you@example.com"
                    />
                    {preFilledFields.email && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <Check className="h-4 w-4 text-green-500" />
                      </div>
                    )}
                  </div>
                  {fieldErrors.email && touched.email && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.email}</p>
                  )}
                </div>



                {/* Company Name */}
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Company Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="companyName"
                      name="companyName"
                      type="text"
                      required
                      value={formData.companyName}
                      onChange={handleChange}
                      onBlur={() => handleFieldBlur('companyName')}
                      className={`pl-10 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${
                        fieldErrors.companyName && touched.companyName
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-gray-300 dark:border-gray-700 focus:ring-gray-300 dark:focus:ring-gray-600'
                      }`}
                      placeholder="Enter your company name"
                    />
                  </div>
                  {fieldErrors.companyName && touched.companyName && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.companyName}</p>
                  )}
                </div>

                {/* Brands Management */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Brand Names
                  </label>
                  <div className="space-y-3">
                    {brands.map((brand, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="flex-1 relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Building className="h-4 w-4 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            value={brand}
                            onChange={(e) => updateBrand(index, e.target.value)}
                            onBlur={handleBrandBlur}
                            className={`pl-9 w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm ${
                              fieldErrors.brands && touched.brands && index === 0 && !brand.trim()
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-300 dark:border-gray-700 focus:ring-gray-300 dark:focus:ring-gray-600'
                            }`}
                            placeholder={`Brand ${index + 1} name`}
                            required={index === 0}
                          />
                        </div>
                        {brands.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeBrand(index)}
                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}

                    {/* Add new brand */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Building className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          value={newBrand}
                          onChange={(e) => setNewBrand(e.target.value)}
                          className="pl-9 w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                          placeholder="Add another brand"
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addBrand())}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={addBrand}
                        disabled={!newBrand.trim()}
                        className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  {fieldErrors.brands && touched.brands && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.brands}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Add all the brands under your company. Each brand will have its own analytics dashboard.
                  </p>
                </div>


              </>
            )}

            {/* Step 2: Service Connections */}
            {step === 2 && (
              <>
                <div>
                  <label htmlFor="brandSelect" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Select Brand
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      id="brandSelect"
                      name="brandSelect"
                      value={selectedBrandForConnection}
                      onChange={(e) => setSelectedBrandForConnection(e.target.value)}
                      className="pl-10 w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    >
                      <option value="">Select a brand to connect services</option>
                      {brandIds.map((brand) => (
                        <option key={brand.brandId} value={brand.brandId}>
                          {brand.brandName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Choose a brand to connect your review and e-commerce platforms
                  </p>
                </div>

                {selectedBrandForConnection && (
                  <div>
                    <h3 className="text-lg font-medium mb-4">
                      Connect services for {brandIds.find(b => b.brandId === selectedBrandForConnection)?.brandName}
                    </h3>

                    {loadingConnections && (
                      <div className="text-center py-4 mb-4">
                        <div className="inline-flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 dark:border-gray-100 mr-2"></div>
                          <span className="text-sm text-gray-600 dark:text-gray-400">Loading connection status...</span>
                        </div>
                      </div>
                    )}

                    <div className="space-y-3">
                      {/* E-commerce Platform - Always show first */}
                      <div className="mb-6">
                        <h4 className="text-md font-medium mb-3 text-gray-700 dark:text-gray-300">E-commerce Platform</h4>

                        {/* Shopify */}
                        <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                          <div className="flex items-center">
                            <div className="flex items-center justify-center h-8 w-8 rounded-full bg-green-600">
                              <svg viewBox="0 0 24 24" width="16" height="16" fill="white">
                                <path d="M15.337 23.979l7.216-1.561s-2.604-17.613-2.625-17.73c-.018-.116-.114-.192-.211-.192s-1.929-.136-1.929-.136-1.275-1.274-1.439-1.411c-.045-.037-.075-.055-.121-.055l-.914 19.085z"/>
                              </svg>
                            </div>
                            <span className="ml-3 font-medium">Shopify</span>
                          </div>
                          {serviceConnections.shopify?.[selectedBrandForConnection] ? (
                            <div className="py-1.5 px-4 bg-green-600 text-white text-sm font-medium rounded-md flex items-center">
                              <Check className="h-4 w-4 mr-1" />
                              Connected
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleServiceConnection('shopify', selectedBrandForConnection, brandIds.find(b => b.brandId === selectedBrandForConnection)?.brandName || '');
                              }}
                              disabled={connectingService === 'shopify'}
                              className="py-1.5 px-4 bg-gray-900 dark:bg-gray-100 dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50"
                            >
                              {connectingService === 'shopify' ? 'Connecting...' : 'Connect'}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Review Platforms - Only show when Shopify is connected */}
                      {isShopifyConnectedForBrand(selectedBrandForConnection) && (
                        <div className="mb-6">
                          <h4 className="text-md font-medium mb-3 text-gray-700 dark:text-gray-300">
                            Review Platforms <span className="text-sm text-gray-500">(Choose one)</span>
                          </h4>
                          <div className="space-y-3">
                            {/* Judge.me */}
                            <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                              <div className="flex items-center">
                                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-teal-600">
                                  <span className="text-white font-bold">J</span>
                                </div>
                                <span className="ml-3 font-medium">Judge.me</span>
                              </div>
                              {serviceConnections.judgeme?.[selectedBrandForConnection] ? (
                                <div className="py-1.5 px-4 bg-green-600 text-white text-sm font-medium rounded-md flex items-center">
                                  <Check className="h-4 w-4 mr-1" />
                                  Connected
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleServiceConnection('judgeme', selectedBrandForConnection, brandIds.find(b => b.brandId === selectedBrandForConnection)?.brandName || '');
                                  }}
                                  disabled={connectingService === 'judgeme' || serviceConnections.yotpo?.[selectedBrandForConnection]}
                                  className="py-1.5 px-4 bg-gray-900 dark:bg-gray-100 dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50"
                                >
                                  {connectingService === 'judgeme' ? 'Connecting...' :
                                   serviceConnections.yotpo?.[selectedBrandForConnection] ? 'Yotpo Connected' : 'Connect'}
                                </button>
                              )}
                            </div>

                            {/* Yotpo */}
                            <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                              <div className="flex items-center">
                                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-500">
                                  <span className="text-white font-bold">Y</span>
                                </div>
                                <span className="ml-3 font-medium">Yotpo</span>
                              </div>
                              {serviceConnections.yotpo?.[selectedBrandForConnection] ? (
                                <div className="py-1.5 px-4 bg-green-600 text-white text-sm font-medium rounded-md flex items-center">
                                  <Check className="h-4 w-4 mr-1" />
                                  Connected
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleServiceConnection('yotpo', selectedBrandForConnection, brandIds.find(b => b.brandId === selectedBrandForConnection)?.brandName || '');
                                  }}
                                  disabled={connectingService === 'yotpo' || serviceConnections.judgeme?.[selectedBrandForConnection]}
                                  className="py-1.5 px-4 bg-gray-900 dark:bg-gray-100 dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50"
                                >
                                  {connectingService === 'yotpo' ? 'Connecting...' :
                                   serviceConnections.judgeme?.[selectedBrandForConnection] ? 'Judge.me Connected' : 'Connect'}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Social Media Platforms - Always show */}
                      <div className="mb-6">
                        <h4 className="text-md font-medium mb-3 text-gray-700 dark:text-gray-300">Social Media Platforms</h4>
                        <div className="space-y-3">
                          {/* Instagram */}
                          <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                            <div className="flex items-center">
                              <div className="flex items-center justify-center h-8 w-8 rounded-full bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-600">
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="white">
                                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                </svg>
                              </div>
                              <span className="ml-3 font-medium">Instagram</span>
                            </div>
                            {serviceConnections.instagram?.[selectedBrandForConnection] ? (
                              <div className="py-1.5 px-4 bg-green-600 text-white text-sm font-medium rounded-md flex items-center">
                                <Check className="h-4 w-4 mr-1" />
                                Connected
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleServiceConnection('instagram', selectedBrandForConnection, brandIds.find(b => b.brandId === selectedBrandForConnection)?.brandName || '');
                                }}
                                disabled={connectingService === 'instagram'}
                                className="py-1.5 px-4 bg-gray-900 dark:bg-gray-100 dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50"
                              >
                                {connectingService === 'instagram' ? 'Connecting...' : 'Connect'}
                              </button>
                            )}
                          </div>

                          {/* Facebook */}
                          <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                            <div className="flex items-center">
                              <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-600">
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="white">
                                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                </svg>
                              </div>
                              <span className="ml-3 font-medium">Facebook</span>
                            </div>
                            {serviceConnections.facebook?.[selectedBrandForConnection] ? (
                              <div className="py-1.5 px-4 bg-green-600 text-white text-sm font-medium rounded-md flex items-center">
                                <Check className="h-4 w-4 mr-1" />
                                Connected
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleServiceConnection('facebook', selectedBrandForConnection, brandIds.find(b => b.brandId === selectedBrandForConnection)?.brandName || '');
                                }}
                                disabled={connectingService === 'facebook'}
                                className="py-1.5 px-4 bg-gray-900 dark:bg-gray-100 dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50"
                              >
                                {connectingService === 'facebook' ? 'Connecting...' : 'Connect'}
                              </button>
                            )}
                          </div>

                          {/* TikTok */}
                          <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                            <div className="flex items-center">
                              <div className="flex items-center justify-center h-8 w-8 rounded-full bg-black">
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="white">
                                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                                </svg>
                              </div>
                              <span className="ml-3 font-medium">TikTok</span>
                            </div>
                            {serviceConnections.tiktok?.[selectedBrandForConnection] ? (
                              <div className="py-1.5 px-4 bg-green-600 text-white text-sm font-medium rounded-md flex items-center">
                                <Check className="h-4 w-4 mr-1" />
                                Connected
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleServiceConnection('tiktok', selectedBrandForConnection, brandIds.find(b => b.brandId === selectedBrandForConnection)?.brandName || '');
                                }}
                                disabled={connectingService === 'tiktok'}
                                className="py-1.5 px-4 bg-gray-900 dark:bg-gray-100 dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50"
                              >
                                {connectingService === 'tiktok' ? 'Connecting...' : 'Connect'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Communication Platforms */}
                      <div className="mb-6">
                        <h4 className="text-md font-medium mb-3 text-gray-700 dark:text-gray-300">Communication Platforms</h4>
                        <div className="space-y-3">
                          {/* WhatsApp */}
                          <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                            <div className="flex items-center">
                              <div className="flex items-center justify-center h-8 w-8 rounded-full bg-green-500">
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="white">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.488"/>
                                </svg>
                              </div>
                              <span className="ml-3 font-medium">WhatsApp Business</span>
                            </div>
                            {serviceConnections.whatsapp?.[selectedBrandForConnection] ? (
                              <div className="py-1.5 px-4 bg-green-600 text-white text-sm font-medium rounded-md flex items-center">
                                <Check className="h-4 w-4 mr-1" />
                                Connected
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleServiceConnection('whatsapp', selectedBrandForConnection, brandIds.find(b => b.brandId === selectedBrandForConnection)?.brandName || '');
                                }}
                                disabled={connectingService === 'whatsapp'}
                                className="py-1.5 px-4 bg-gray-900 dark:bg-gray-100 dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50"
                              >
                                {connectingService === 'whatsapp' ? 'Connecting...' : 'Connect'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Analytics Platforms */}
                      <div className="mb-6">
                        <h4 className="text-md font-medium mb-3 text-gray-700 dark:text-gray-300">Analytics Platforms</h4>
                        <div className="space-y-3">
                          {/* Google Analytics */}
                          <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                            <div className="flex items-center">
                              <div className="flex items-center justify-center h-8 w-8 rounded-full bg-orange-500">
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="white">
                                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                </svg>
                              </div>
                              <span className="ml-3 font-medium">Google Analytics</span>
                            </div>
                            {serviceConnections.google?.[selectedBrandForConnection] ? (
                              <div className="py-1.5 px-4 bg-green-600 text-white text-sm font-medium rounded-md flex items-center">
                                <Check className="h-4 w-4 mr-1" />
                                Connected
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleServiceConnection('google', selectedBrandForConnection, brandIds.find(b => b.brandId === selectedBrandForConnection)?.brandName || '');
                                }}
                                disabled={connectingService === 'google'}
                                className="py-1.5 px-4 bg-gray-900 dark:bg-gray-100 dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50"
                              >
                                {connectingService === 'google' ? 'Connecting...' : 'Connect'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {!selectedBrandForConnection && (
                  <div className="text-center py-8">
                    <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">
                      Please select a brand above to connect your services
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Step 3: Confirmation */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <h3 className="text-lg font-medium mb-4">Review Your Information</h3>

                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Name</span>
                      <span className="text-sm font-medium">{formData.firstName} {formData.lastName}</span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Email</span>
                      <span className="text-sm font-medium">{formData.email}</span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Company</span>
                      <span className="text-sm font-medium">{formData.companyName}</span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Company Size</span>
                      <span className="text-sm font-medium">{formData.companySize}</span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Industry</span>
                      <span className="text-sm font-medium">{formData.industry}</span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Role</span>
                      <span className="text-sm font-medium">{formData.role}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    id="terms"
                    name="terms"
                    type="checkbox"
                    required
                    className="h-4 w-4 text-gray-900 dark:text-gray-100 focus:ring-gray-500 border-gray-300 dark:border-gray-700 rounded"
                  />
                  <label htmlFor="terms" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    I agree to the <a href="#" className="text-gray-900 dark:text-white font-medium hover:underline">Terms of Service</a> and <a href="#" className="text-gray-900 dark:text-white font-medium hover:underline">Privacy Policy</a>
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    id="marketing"
                    name="marketing"
                    type="checkbox"
                    className="h-4 w-4 text-gray-900 dark:text-gray-100 focus:ring-gray-500 border-gray-300 dark:border-gray-700 rounded"
                  />
                  <label htmlFor="marketing" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    I'd like to receive product updates and marketing communications
                  </label>
                </div>
              </div>
            )}

            <div className="flex justify-between">
              {step > 1 ? (
                <button
                  type="button"
                  onClick={handlePrevStep}
                  className="flex items-center justify-center py-2 px-4 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 transform active:scale-95"
                >
                  Back
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => logout()}
                  className="flex items-center justify-center py-2 px-4 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 transform active:scale-95"
                >
                  Sign out
                </button>
              )}

              {step < 3 ? (
                <div className="flex gap-3">
                  {/* Skip to Dashboard - Available on all steps */}
                  <button
                    type="button"
                    onClick={handleSkipToDashboard}
                    className="flex items-center justify-center py-2 px-4 border border-yellow-500 rounded-lg shadow-sm text-sm font-medium text-yellow-600 bg-yellow-50 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-all duration-200"
                  >
                    🚀 Skip to Dashboard
                  </button>

                  <button
                    type="button"
                    onClick={handleNextStep}
                    disabled={
                      isSaving ||
                      (step === 1 && !isStep1Complete()) ||
                      (step === 2 && !canProceedToStep3())
                    }
                    className={`flex items-center justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 transform active:scale-95 ${
                      isSaving ||
                      (step === 1 && !isStep1Complete()) ||
                      (step === 2 && !canProceedToStep3())
                        ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        : 'text-white bg-gray-900 dark:bg-gray-100 dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white focus:ring-gray-500'
                    }`}
                  >
                    {isSaving ? 'Saving...' : 'Next'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex gap-3">
                  {/* TEMPORARY: Skip to Dashboard - FOR TESTING ONLY */}
                  <button
                    type="button"
                    onClick={handleSkipToDashboard}
                    className="flex items-center justify-center py-2 px-4 border border-yellow-500 rounded-lg shadow-sm text-sm font-medium text-yellow-600 bg-yellow-50 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-all duration-200"
                  >
                    🚀 Skip to Dashboard
                  </button>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex items-center justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gray-900 dark:bg-gray-100 dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 transform active:scale-95"
                  >
                    {isLoading ? (
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white dark:text-gray-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : null}
                    {isLoading ? "Creating account..." : "Create account"}
                  </button>
                </div>
              )}
            </div>
          </form>
        </div>

        <div className="mt-8 text-center text-xs text-gray-500 dark:text-gray-400">
          <p>© 2025 Bizamply Analytics. All rights reserved.</p>
        </div>
      </div>

      {/* Connection Popup Modal */}
      {showConnectionPopup && (
        <>
          <style>{`
            @keyframes modalFadeIn {
              from {
                opacity: 0;
                transform: scale(0.95) translateY(-10px);
              }
              to {
                opacity: 1;
                transform: scale(1) translateY(0);
              }
            }
          `}</style>
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999]"
          style={{ zIndex: 9999 }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !connectingInPopup) {
              setShowConnectionPopup(false);
              setConnectingInPopup(false);
            }
          }}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl border border-gray-200 dark:border-gray-700 relative z-[10000] transform transition-all duration-200 ease-out scale-100 opacity-100"
            style={{
              zIndex: 10000,
              animation: 'modalFadeIn 0.2s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                Connect {connectionData.serviceName.charAt(0).toUpperCase() + connectionData.serviceName.slice(1)}
              </h3>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowConnectionPopup(false);
                  setConnectingInPopup(false);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Connect {connectionData.serviceName} for <strong>{connectionData.brandName}</strong>
            </p>

            <div className="space-y-4">
              {connectionData.serviceName === 'shopify' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Shopify Store Domain
                  </label>
                  <input
                    type="text"
                    value={shopifyDomain}
                    onChange={(e) => setShopifyDomain(e.target.value)}
                    placeholder="yourstore.myshopify.com"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Enter your Shopify store domain (e.g., yourstore.myshopify.com)
                  </p>
                </div>
              )}

              {connectionData.serviceName === 'judgeme' && (
                <div>
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-600 dark:text-blue-400">
                          <strong>Shopify Required:</strong> Judge.me will automatically use your Shopify store domain. Make sure Shopify is connected first.
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Click connect to authorize Judge.me integration for {connectionData.brandName}. Your Shopify store domain will be used automatically.
                  </p>
                </div>
              )}

              {connectionData.serviceName === 'yotpo' && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Click connect to authorize Yotpo integration for {connectionData.brandName}.
                  </p>
                </div>
              )}

              {connectionData.serviceName === 'whatsapp' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    WhatsApp Business Number
                  </label>
                  <input
                    type="text"
                    value={whatsappNumber}
                    onChange={(e) => setWhatsappNumber(e.target.value)}
                    placeholder="+1234567890"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Enter your WhatsApp Business phone number with country code
                  </p>
                </div>
              )}

              {connectionData.serviceName === 'instagram' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Instagram Handle
                  </label>
                  <input
                    type="text"
                    value={instagramHandle}
                    onChange={(e) => setInstagramHandle(e.target.value)}
                    placeholder="@yourbrand"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Enter your Instagram business account handle
                  </p>
                </div>
              )}

              {connectionData.serviceName === 'facebook' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Facebook Page ID
                  </label>
                  <input
                    type="text"
                    value={facebookPageId}
                    onChange={(e) => setFacebookPageId(e.target.value)}
                    placeholder="123456789012345"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Enter your Facebook Business Page ID
                  </p>
                </div>
              )}

              {connectionData.serviceName === 'tiktok' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    TikTok Handle
                  </label>
                  <input
                    type="text"
                    value={tiktokHandle}
                    onChange={(e) => setTiktokHandle(e.target.value)}
                    placeholder="@yourbrand"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Enter your TikTok business account handle
                  </p>
                </div>
              )}

              {connectionData.serviceName === 'google' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Google Analytics Property ID
                  </label>
                  <input
                    type="text"
                    value={googleBusinessId}
                    onChange={(e) => setGoogleBusinessId(e.target.value)}
                    placeholder="GA4-XXXXXXXXX-X"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Enter your Google Analytics 4 Property ID
                  </p>
                </div>
              )}
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowConnectionPopup(false);
                  setConnectingInPopup(false);
                }}
                disabled={connectingInPopup}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handlePopupConnection();
                }}
                disabled={connectingInPopup}
                className="flex-1 px-4 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {connectingInPopup ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white dark:border-gray-900 mr-2"></div>
                    Connecting...
                  </>
                ) : (
                  'Connect'
                )}
              </button>
            </div>
          </div>
        </div>
        </>
      )}
    </div>
  );
}

export default function OnboardingPage() {
  return (
    <ProtectedRoute>
      <OnboardingPageContent />
    </ProtectedRoute>
  );
}